import React, { useState, useEffect } from 'react';
import Modal from '../ui/Modal';
import { boardAPI, eventsAPI } from '../../services/api';
import { BoardPost, BoardPermissions } from '../../types/board';
import type { Event } from '../../types/event';
import AiButton from '../ai/AiButton';
import HelpHint from '../help/HelpHint';
import { useAiStatus, refreshAiStatus } from '../../hooks/useAiStatus';
import { aiAPI } from '../../services/api';
import { getErrorMessage } from '../../utils/errorMessage';
import type { AiAnnouncementTone } from '../../types/ai';

const TONE_OPTIONS: { value: AiAnnouncementTone; label: string }[] = [
  { value: 'friendly', label: 'Barátságos' },
  { value: 'formal', label: 'Hivatalos' },
  { value: 'short', label: 'Rövid' },
];

interface BoardPostFormProps {
  isOpen: boolean;
  onClose: () => void;
  /** Szerkesztendő bejegyzés, vagy null új bejegyzéshez. */
  post?: BoardPost | null;
  /** Mit tehet a felhasználó: címzési lehetőségek, kitűzés, írható korosztályok. */
  permissions: BoardPermissions | null;
  onSaved: () => void;
  /** Új bejegyzésnél ehhez az eseményhez kötve nyílik (pl. az esemény oldaláról). */
  initialEventId?: number | null;
}

const formatEventOption = (startTime: string, title: string, ageGroup?: string) =>
  `${new Date(startTime).toLocaleDateString('hu-HU', { month: 'short', day: 'numeric', weekday: 'short' })} ${new Date(
    startTime
  ).toLocaleTimeString('hu-HU', { hour: '2-digit', minute: '2-digit' })} · ${title}${ageGroup ? ` (${ageGroup})` : ''}`;

/** yyyy-MM-dd a date inputhoz, a lejárat napjából. */
const toDateInput = (value?: string | null): string =>
  value ? new Date(value).toISOString().split('T')[0] : '';

const BoardPostForm: React.FC<BoardPostFormProps> = ({ isOpen, onClose, post, permissions, onSaved, initialEventId }) => {
  const canPostClubWide = permissions?.canPostClubWide ?? false;
  const canPostForGuests = permissions?.canPostForGuests ?? false;
  const canPin = permissions?.canPin ?? false;

  const [content, setContent] = useState('');
  const [isClubWide, setIsClubWide] = useState(false);
  const [isForGuests, setIsForGuests] = useState(false);
  const [ageGroupIds, setAgeGroupIds] = useState<number[]>([]);
  const [isPinned, setIsPinned] = useState(false);
  const [expiresAt, setExpiresAt] = useState('');
  const [commentsEnabled, setCommentsEnabled] = useState(true);
  const [notify, setNotify] = useState(true);
  const [eventId, setEventId] = useState<number | null>(null);
  const [events, setEvents] = useState<Event[]>([]);

  // Csak azok a korosztályok, amiknek a felhasználó a klub beállítása szerint
  // írhat - az edző a sajátjait, a játékos/szülő a saját (gyermeke) korosztályát.
  const availableAgeGroups = permissions?.ageGroups ?? [];
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // AI bejelentés-író: pár kulcsszóból (vagy a meglévő szöveg átírásával) kész szöveg
  const ai = useAiStatus();
  const [aiOpen, setAiOpen] = useState(false);
  const [aiNotes, setAiNotes] = useState('');
  const [aiTone, setAiTone] = useState<AiAnnouncementTone>('friendly');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  const writeWithAi = async () => {
    if (!aiNotes.trim() && !content.trim()) {
      setAiError('Írd le pár szóban, miről szóljon a bejelentés.');
      return;
    }
    setAiLoading(true);
    setAiError(null);
    try {
      const result = await aiAPI.announcement({
        notes: aiNotes.trim(),
        tone: aiTone,
        eventId,
        // Kulcsszavak nélkül a meglévő szöveget írja át
        currentText: aiNotes.trim() ? null : content,
      });
      setContent(result.content);
    } catch (err) {
      setAiError(getErrorMessage(err, 'Az AI most nem tudott szöveget írni.'));
    } finally {
      setAiLoading(false);
      refreshAiStatus();
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    if (post) {
      setContent(post.content);
      setIsClubWide(post.isClubWide);
      setIsForGuests(post.isForGuests);
      setAgeGroupIds(post.ageGroups.map((ag) => ag.id));
      setIsPinned(post.isPinned);
      setExpiresAt(toDateInput(post.expiresAt));
      setCommentsEnabled(post.commentsEnabled);
    } else {
      setContent('');
      setIsClubWide(false);
      setIsForGuests(false);
      setAgeGroupIds([]);
      setIsPinned(false);
      // A lejárat alapból üres: a legtöbb bejegyzés magától lecsúszik a falról.
      setExpiresAt('');
      setCommentsEnabled(true);
    }

    setNotify(true);
    setError(null);
    setAiOpen(false);
    setAiNotes('');
    setAiError(null);
    setEventId(post ? post.event?.id ?? null : initialEventId ?? null);
    // A választható események: a következő két hónap, amit a felhasználó lát
    eventsAPI
      .getUpcoming(60)
      .then(setEvents)
      .catch(() => setEvents([]));
  }, [isOpen, post, initialEventId]);

  // Eseményhez kötve, ha még nincs címzett, az esemény korosztálya a kézenfekvő
  useEffect(() => {
    if (!isOpen || post || eventId == null || ageGroupIds.length > 0 || isClubWide) return;
    const linked = events.find((e) => e.id === eventId);
    if (linked?.ageGroupId && availableAgeGroups.some((ag) => ag.id === linked.ageGroupId)) {
      setAgeGroupIds([linked.ageGroupId]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, eventId, events]);

  // A már kötött, de a listában nem szereplő (pl. lezajlott) esemény is választható maradjon
  const eventOptions = [
    ...(post?.event && !events.some((e) => e.id === post.event!.id)
      ? [{ id: post.event.id, label: formatEventOption(post.event.startTime, post.event.title) }]
      : []),
    ...events.map((e) => ({ id: e.id, label: formatEventOption(e.startTime, e.title, e.ageGroup || e.guestTeam || undefined) })),
  ];

  const toggleAgeGroup = (id: number) => {
    setAgeGroupIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!content.trim()) {
      setError('Írd meg, mit szeretnél közölni.');
      return;
    }

    if (!isClubWide && !isForGuests && ageGroupIds.length === 0) {
      setError('Válassz legalább egy korosztályt, vagy jelöld klubszintűnek.');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // A lejárat a megadott nap végéig érvényes, nem a nap 0. percéig
      const expiry = expiresAt ? `${expiresAt}T23:59:59` : null;

      if (post) {
        await boardAPI.update(post.id, {
          content: content.trim(),
          isClubWide,
          isForGuests,
          ageGroupIds: isClubWide ? [] : ageGroupIds,
          isPinned,
          expiresAt: expiry,
          clearExpiresAt: !expiry,
          commentsEnabled,
          eventId: eventId ?? undefined,
          clearEvent: eventId == null && !!post.event,
        });
      } else {
        await boardAPI.create({
          content: content.trim(),
          isClubWide,
          isForGuests,
          ageGroupIds: isClubWide ? [] : ageGroupIds,
          isPinned,
          expiresAt: expiry,
          commentsEnabled,
          notify,
          eventId,
        });
      }

      onSaved();
      onClose();
    } catch (err: any) {
      setError(getErrorMessage(err, 'A mentés nem sikerült.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={post ? 'Bejegyzés szerkesztése' : 'Új bejegyzés'}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="board-content" className="block text-sm font-medium text-gray-700 mb-2">
            Mit szeretnél közölni? *
          </label>
          <textarea
            id="board-content"
            rows={5}
            maxLength={2000}
            required
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="pl. A holnapi edzés 17:00 helyett 18:00-kor kezdődik."
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          <div className="mt-1 flex items-center justify-between gap-2">
            {ai.available ? (
              <span className="inline-flex items-center gap-1">
                <AiButton variant="link" onClick={() => setAiOpen((v) => !v)}>
                  Megírás AI-val
                </AiButton>
                <HelpHint id="board.aiWriter" />
              </span>
            ) : (
              <span />
            )}
            <p className="text-xs text-gray-400">{content.length} / 2000</p>
          </div>

          {ai.available && aiOpen && (
            <div className="mt-2 space-y-2 rounded-md border border-violet-200 bg-violet-50 p-3">
              <label htmlFor="board-ai-notes" className="block text-sm font-medium text-gray-700">
                Mit kell közölni? (kulcsszavakban)
              </label>
              <textarea
                id="board-ai-notes"
                rows={2}
                maxLength={1500}
                value={aiNotes}
                onChange={(e) => setAiNotes(e.target.value)}
                placeholder="pl. szombat 9:00 Debrecen, gyülekező 7:30 a pályánál, sípcsontvédő kötelező"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <p className="text-xs text-gray-500">
                Ha üresen hagyod, a fenti szöveget írja át a választott hangnemben. Ha eseményt választasz lent, annak
                időpontját és helyszínét is beleírja.
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <select
                  value={aiTone}
                  onChange={(e) => setAiTone(e.target.value as AiAnnouncementTone)}
                  aria-label="Hangnem"
                  className="rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {TONE_OPTIONS.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
                <AiButton variant="solid" onClick={writeWithAi} loading={aiLoading} loadingText="Írom…" className="ml-auto">
                  {aiNotes.trim() || !content.trim() ? 'Megírás' : 'Átírás'}
                </AiButton>
              </div>
              {aiError && <p className="text-sm text-red-600">{aiError}</p>}
            </div>
          )}
        </div>

        <div>
          <span className="block text-sm font-medium text-gray-700 mb-2">Kinek szól? *</span>

          {canPostClubWide && (
            <label className="flex items-center gap-2 mb-2">
              <input
                type="checkbox"
                checked={isClubWide}
                onChange={(e) => setIsClubWide(e.target.checked)}
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">Az egész klubnak</span>
            </label>
          )}

          {canPostForGuests && (
            <label className="flex items-center gap-2 mb-2">
              <input
                type="checkbox"
                checked={isForGuests}
                onChange={(e) => setIsForGuests(e.target.checked)}
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">A vendégeknek (gondnok, kitmanager, külsős csapatok)</span>
            </label>
          )}

          {!isClubWide && (
            <div className="space-y-1 rounded-md border border-gray-200 p-2 max-h-48 overflow-y-auto">
              {availableAgeGroups.length === 0 ? (
                <p className="text-sm text-gray-500">Nincs korosztály, amihez írhatnál.</p>
              ) : (
                availableAgeGroups.map((ag) => (
                  <label key={ag.id} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={ageGroupIds.includes(ag.id)}
                      onChange={() => toggleAgeGroup(ag.id)}
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">{ag.name}</span>
                  </label>
                ))
              )}
            </div>
          )}
        </div>

        <div>
          <label htmlFor="board-event" className="block text-sm font-medium text-gray-700 mb-2">
            Eseményhez kapcsolódik?
          </label>
          <select
            id="board-event"
            value={eventId ?? ''}
            onChange={(e) => setEventId(e.target.value ? Number(e.target.value) : null)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Nem kapcsolódik eseményhez</option>
            {eventOptions.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500">Az esemény oldalán is megjelenik, és onnan egy kattintással elérhető.</p>
        </div>

        <div className="space-y-2">
          {canPin && (
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={isPinned}
                onChange={(e) => setIsPinned(e.target.checked)}
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">Kitűzöm a fal tetejére</span>
            </label>
          )}

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={commentsEnabled}
              onChange={(e) => setCommentsEnabled(e.target.checked)}
              className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700">Lehet hozzászólni</span>
          </label>

          {!post && (
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={notify}
                onChange={(e) => setNotify(e.target.checked)}
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">Értesítés küldése a címzetteknek</span>
            </label>
          )}
        </div>

        <div>
          <label htmlFor="board-expires" className="block text-sm font-medium text-gray-700 mb-2">
            Meddig maradjon kint?
          </label>
          <input
            type="date"
            id="board-expires"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          <p className="mt-1 text-xs text-gray-500">
            Üresen hagyva nem jár le; a régi bejegyzések maguktól lecsúsznak a falról.
          </p>
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Mégse
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50"
          >
            {saving ? 'Mentés...' : post ? 'Mentés' : 'Közzététel'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default BoardPostForm;
